/* Cart drawer + cart page behaviour.
   - <cart-drawer> opens from #cart-icon-bubble and on `cart:open`.
   - Quantity / remove / coupon handlers are delegated on document so the cart
     PAGE (no <cart-drawer> present) reuses them.
   - Refresh re-renders ONLY the cart content (not the <dialog>), via the
     Section Rendering API, so the open drawer is never destroyed mid-flight.
   - A single in-flight guard (`busy`) prevents overlapping mutations. */

const ROOT = window.Shopify?.routes?.root || '/';

let busy = false;

/* Which cart section to re-render depends on context: the cart page renders
   `main-cart`; everywhere else the drawer renders `cart-drawer`. */
function sectionList() {
  return document.querySelector('.cart-page') ? 'main-cart' : 'cart-drawer';
}

function setBusy(state) {
  busy = state;
  for (const root of document.querySelectorAll('[data-cart-root]')) {
    root.toggleAttribute('data-cart-loading', state);
  }
}

function parse(html, selector) {
  if (!html) return null;
  return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
}

/* Swap content in place, leaving the <dialog> element (and its open state)
   untouched. Drawer: replace the dialog's inner content. Page: replace the
   cart root. */
function renderSections(sections) {
  if (!sections) return;

  if (sections['cart-drawer']) {
    const newInner = parse(sections['cart-drawer'], '.cart-drawer__inner');
    const curInner = document.querySelector('cart-drawer .cart-drawer__inner');
    if (newInner && curInner) curInner.innerHTML = newInner.innerHTML;
  }

  if (sections['main-cart']) {
    const newRoot = parse(sections['main-cart'], '[data-cart-root]');
    const curRoot = document.querySelector('.cart-page [data-cart-root]');
    if (newRoot && curRoot) curRoot.replaceWith(newRoot);
  }

  syncBubble();

  /* Every cart mutation funnels through renderSections (changeLine calls it
     directly, refreshCart/cart:refresh call it too), so this is the single
     choke point where the gift-card UI must be re-applied: the re-rendered
     HTML has no gift-card pills/deduction (gift cards aren't in the Liquid
     cart), so we re-read the session cart's applied gift cards and repaint.
     Async, fire-and-forget, and fully isolated (gcSafeApply swallows any
     sync throw / async rejection) — a gift-card failure must NEVER break the
     core cart re-render. */
  gcSafeApply();
}

/* Mirror the freshly-rendered cart item count into the header cart bubble. */
function syncBubble() {
  const root = document.querySelector('cart-drawer [data-cart-root]')
    || document.querySelector('.cart-page [data-cart-root]');
  if (!root) return;
  const count = Number(root.dataset.count || '0');
  const bubble = document.querySelector('#cart-icon-bubble .cart-bubble');
  if (bubble) {
    bubble.textContent = count;
    bubble.toggleAttribute('hidden', count < 1);
  }
}

async function fetchAndRender() {
  const res = await fetch(`${ROOT}?sections=${sectionList()}`);
  if (res.ok) renderSections(await res.json());
}

/* Gift With Purchase (optional — gwp-manager.js is only loaded while the promo
   is on) may add, clamp or remove the gift after a quantity change. Awaited
   INSIDE the busy window so the next qty/remove click never uses a line index
   shifted by a gift change. Never throws: a GWP failure must not break the cart. */
async function syncGift(source) {
  if (typeof window.processGWP !== 'function') return false;
  try {
    const result = await window.processGWP(null, source);
    return !!(result && result.changed);
  } catch (e) {
    return false;
  }
}

async function refreshCart(providedSections) {
  if (busy) return;
  setBusy(true);
  try {
    // Callers that already fetched sections (e.g. the discount form) pass them
    // in to avoid a second round-trip; otherwise re-fetch the current cart.
    if (providedSections) {
      renderSections(providedSections);
    } else {
      await fetchAndRender();
    }
  } catch (e) {
    console.error('cart refresh failed', e);
  } finally {
    setBusy(false);
  }
}

/* Bound at module scope (not inside <cart-drawer>) so a cart:refresh re-render
   also fires on the cart PAGE, where the drawer element is not rendered
   (theme.liquid only injects <cart-drawer> off the cart page). Without this,
   JS adds on the page — e.g. the FBT "frequently bought together" cards — would
   not re-render the cart. */
document.addEventListener('cart:refresh', (e) => refreshCart(e.detail && e.detail.sections));

async function changeLine(line, quantity) {
  if (busy) return;
  setBusy(true);
  try {
    const res = await fetch(`${ROOT}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity, sections: sectionList() }),
    });
    if (res.ok) {
      const data = await res.json();
      renderSections(data.sections);
      if (await syncGift('cart-change')) await fetchAndRender();
    }
  } catch (e) {
    console.error('cart change failed', e);
  } finally {
    setBusy(false);
  }
}

class CartDrawer extends HTMLElement {
  #lockedY = 0;

  connectedCallback() {
    this.dialog = this.querySelector('.cart-drawer__dialog');
    this.#bindOpener();
    this.dialog?.addEventListener('click', (e) => this.#onDialogClick(e));
    this.dialog?.addEventListener('close', () => this.#unlock());
    document.addEventListener('cart:open', () => this.open());
  }

  #bindOpener() {
    const icon = document.querySelector('#cart-icon-bubble');
    if (!icon || icon.dataset.cartBound) return;
    icon.dataset.cartBound = 'true';
    icon.setAttribute('role', 'button');
    icon.addEventListener('click', (e) => { e.preventDefault(); document.dispatchEvent(new CustomEvent('cart:open')); });
  }

  /* A Klaviyo Back-in-Stock trigger inside an FBT card opens Klaviyo's own modal
     on <body>, which renders UNDER our <dialog> top layer and is invisible behind
     the drawer. Close the drawer so the BIS modal is reachable. Deferred via rAF
     so Klaviyo's own <body> click listener processes THIS same click first — we
     must not stopPropagation. Mirrors the Look bundle drawer (look-bundle-popup.js). */
  #onDialogClick(e) {
    if (e.target.closest('.klaviyo-bis-trigger')) {
      requestAnimationFrame(() => this.close());
      return;
    }
    if (e.target === this.dialog) this.close();
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    this.#lockedY = window.scrollY;
    document.documentElement.style.setProperty('--cart-scroll-y', `${this.#lockedY}px`);
    document.documentElement.classList.add('cart-open');
    this.dialog.showModal();
  }

  close() {
    if (!this.dialog?.open) return;
    this.dialog.close();
  }

  #unlock() {
    if (!document.documentElement.classList.contains('cart-open')) return;
    document.documentElement.classList.remove('cart-open');
    document.documentElement.style.removeProperty('--cart-scroll-y');
    window.scrollTo({ top: this.#lockedY || 0, behavior: 'instant' });
  }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

/* Delegated handlers — work in the drawer AND on the cart page. All cart
   mutations route through changeLine(), which is guarded by `busy`. */
document.addEventListener('click', (e) => {
  try {
    if (!(e.target instanceof Element)) return;

    if (e.target.closest('[data-cart-close]')) { document.querySelector('cart-drawer')?.close?.(); return; }

    if (busy) {
      // A mutation is in flight — swallow qty/remove clicks so we never fire
      // overlapping change requests.
      if (e.target.closest('[data-cart-remove],[data-cart-qty]')) { e.preventDefault(); return; }
    }

    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      const line = remove.closest('[data-line]')?.dataset.line;
      if (line) changeLine(line, 0);
      return;
    }

    const qty = e.target.closest('[data-cart-qty]');
    if (qty) {
      const item = qty.closest('[data-line]');
      const current = Number(item?.querySelector('[data-cart-qty-value]')?.textContent.trim() || '0');
      const next = qty.dataset.cartQty === 'plus' ? current + 1 : current - 1;
      if (item?.dataset.line && next >= 0) changeLine(item.dataset.line, next);
    }
  } catch (err) { /* a core cart click error must never break sibling handlers */ }
});

/* Native discount-code form. Applies/removes codes via /cart/update.js (which
   takes a comma-separated `discount` string = the full desired set) and
   re-renders through the same cart:refresh path. No page reload. */
class CartDiscountForm extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="cart-discount"]');
    this.button = this.querySelector('[data-apply-discount]');
    this.errorEl = this.querySelector('[data-discount-error]');

    this.form?.addEventListener('submit', (e) => this.#apply(e));
    this.button?.addEventListener('click', (e) => this.#apply(e));
    for (const pill of this.querySelectorAll('.cart-discount__pill')) {
      pill.addEventListener('click', () => this.#remove(pill.dataset.discountCode));
    }
    this.#restoreDetails();
  }

  /* Persist the discount collapsible's open/closed state. connectedCallback
     re-runs on every cart re-render (the element is re-parsed), and Liquid only
     provides the default `open` (settings.cart_discounts_open_by_default), so a
     manual toggle would be lost on render/reload without this. Stored in
     localStorage → survives cart edits, reloads, new tabs. */
  #restoreDetails() {
    const details = this.querySelector('.cart-discount__details');
    if (!details) return;
    try {
      const saved = localStorage.getItem('dpd_discount_open');
      if (saved !== null) details.open = saved === '1'; // override Liquid default
    } catch (e) { /* storage disabled */ }
    // Bind AFTER restoring so the programmatic set above doesn't self-write.
    details.addEventListener('toggle', () => {
      try { localStorage.setItem('dpd_discount_open', details.open ? '1' : '0'); } catch (e) { /* noop */ }
    });
  }

  #appliedCodes() {
    return [...this.querySelectorAll('.cart-discount__pill')]
      .map((p) => p.dataset.discountCode)
      .filter(Boolean);
  }

  async #apply(e) {
    e?.preventDefault();
    const code = this.input?.value.trim();
    if (!code) { this.#showError(); return; }
    const codes = this.#appliedCodes().filter((c) => c !== code);
    codes.push(code);
    await this.#submit(codes, code);
  }

  async #remove(code) {
    if (!code) return;
    await this.#submit(this.#appliedCodes().filter((c) => c !== code), null);
  }

  async #submit(codes, validateCode) {
    if (this.#busy) return;
    this.#busy = true;
    if (this.button) this.button.disabled = true;
    this.errorEl?.classList.add('hide');
    try {
      const res = await fetch(`${ROOT}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(','), sections: sectionList() }),
      });
      const data = await res.json();

      // The just-entered code isn't a valid discount → try it as a GIFT CARD
      // (a payment tender, applied via the Storefront API — never via
      // /cart/update.js) before surfacing an error. Skip the re-render either
      // way: the Shopify cart is unchanged (a non-applicable code isn't
      // persisted, and a gift card never touches the Liquid cart).
      if (validateCode) {
        const entry = (data.discount_codes || []).find((d) => d.code === validateCode);
        if (entry && entry.applicable === false) {
          const applied = await tryGiftCard(validateCode);
          if (applied) {
            if (this.input) this.input.value = '';
          } else {
            this.#showRefusal(validateCode, false);
          }
          return;
        }
      }
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { sections: data.sections } }));
    } catch (err) {
      console.error('discount update failed', err);
      if (validateCode) this.#showRefusal(validateCode, true);
      else this.#showError(this.dataset.networkError);
    } finally {
      this.#busy = false;
      if (this.button) this.button.disabled = false;
    }
  }

  /* Say WHY a code was refused (assets/discount-error-reason.js). Codes that
     match settings.cart_discounts_customer_prefixes depend on who the shopper
     is: Shopify only knows a guest at checkout, by the email they enter, so a
     guest is told to apply it there instead of being told it's invalid. */
  #showRefusal(code, networkError) {
    const reasonOf = window.dpdDiscountErrorReason;
    const reason = reasonOf
      ? reasonOf({
          code,
          networkError,
          loggedIn: this.dataset.loggedIn === 'true',
          gatedPrefixes: this.dataset.customerPrefixes,
        })
      : (networkError ? 'network' : 'invalid');
    const messages = {
      network: this.dataset.networkError,
      checkout: this.dataset.checkoutError,
      not_eligible: this.dataset.notEligibleError,
      invalid: this.dataset.codeError,
    };
    this.#showError(messages[reason] || this.dataset.codeError);
  }

  #showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.classList.remove('hide');
    const t = this.errorEl.querySelector('.cart-discount__error-text');
    if (t) t.textContent = msg || this.errorEl.dataset.discountErrorMessage || t.textContent;
  }
}
if (!customElements.get('cart-discount-form')) customElements.define('cart-discount-form', CartDiscountForm);

/* ============================================================================
   Gift cards in the cart (Storefront API, tokenless — operates on the SAME cart)

   Gift cards can't be applied through the Liquid/AJAX cart (a gift card is a
   payment tender, not a price cut). But the theme's session cart IS reachable
   via the Storefront GraphQL Cart API: /cart.js returns a `token` that already
   includes the `?key=`, so `gid://shopify/Cart/<token>` is the very same cart
   the native checkout uses. So we apply/remove gift cards directly on that cart
   and let the NATIVE checkout carry them — no separate cart, no redirect, no
   stored codes, no href swapping.

   The cart itself is the source of truth: on load and after every cart render we
   read its appliedGiftCards and paint the pills + subtotal deduction from that.
   Shopify never reduces cart.total_price for a gift card (it's redeemed at
   payment), so we compute the "final" figure ourselves from amountUsed.
   ========================================================================== */

const GC_VERSION = '2026-07';
/* Tiny performance gate (NOT a source of truth): '1' once a gift card has been
   applied on this browser, so carts that never use one do zero network. Self-
   heals — cleared whenever a read/mutation finds no active cards. */
const GC_FLAG = 'dpd_gc_active';
let gcRun = 0;

function gcFlagRead() { try { return localStorage.getItem(GC_FLAG) === '1'; } catch (e) { return false; } }
function gcFlagSet(on) { try { if (on) localStorage.setItem(GC_FLAG, '1'); else localStorage.removeItem(GC_FLAG); } catch (e) { /* noop */ } }

function gcForm() { return document.querySelector('cart-discount-form'); }

function gcEscape(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

/* Dawn-style money formatter using the shop's Liquid format string, so JS
   figures match the Liquid money / money_with_currency output exactly. */
function gcFormatMoney(cents, format) {
  const value = Number(cents) || 0;
  const withDelimiters = (num, precision, thousands, decimal) => {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    const n = (num / 100).toFixed(precision);
    const parts = n.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    return dollars + (parts[1] ? decimal + parts[1] : '');
  };
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = format && format.match(placeholder);
  let out;
  switch (match && match[1]) {
    case 'amount_no_decimals': out = withDelimiters(value, 0); break;
    case 'amount_with_comma_separator': out = withDelimiters(value, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': out = withDelimiters(value, 0, '.', ','); break;
    case 'amount_with_space_separator': out = withDelimiters(value, 2, ' ', ','); break;
    default: out = withDelimiters(value, 2);
  }
  return format ? format.replace(placeholder, out) : out;
}
function gcMoney(cents, withCurrency) {
  const form = gcForm();
  const fmt = withCurrency ? (form && form.dataset.moneyFormatCurrency) : (form && form.dataset.moneyFormat);
  if (fmt) return gcFormatMoney(cents, fmt);
  return ((Number(cents) || 0) / 100).toFixed(2);
}

function gcCardActive(g) {
  return Number(g && g.balance && g.balance.amount) > 0
    || Number(g && g.amountUsed && g.amountUsed.amount) > 0;
}
function gcMatchesLast(g, code) {
  const lc = (g && g.lastCharacters ? g.lastCharacters : '').toUpperCase();
  return !!lc && String(code).toUpperCase().endsWith(lc);
}

/* Storefront GraphQL against the shop domain (tokenless; sends the optional
   data-storefront-token only if present). Returns parsed JSON or null. */
async function gcGraphQL(query, variables) {
  const form = gcForm();
  const shop = (form && form.dataset.shop) || (window.Shopify && window.Shopify.shop);
  if (!shop) return null;
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const token = form && form.dataset.storefrontToken;
  if (token) headers['X-Shopify-Storefront-Access-Token'] = token;
  try {
    const res = await fetch(`https://${shop}/api/${GC_VERSION}/graphql.json`, {
      method: 'POST', headers, body: JSON.stringify({ query, variables }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    console.error('[GC] GraphQL failed', e);
    return null;
  }
}

/* The session cart's Storefront ID. /cart.js `token` already carries `?key=`,
   so this IS the exact cart the native checkout uses. */
async function gcSessionCartId() {
  try {
    const res = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const cart = await res.json();
    return cart && cart.token ? `gid://shopify/Cart/${cart.token}` : null;
  } catch (e) {
    return null;
  }
}

const GC_CARD_FIELDS = 'appliedGiftCards { id lastCharacters amountUsed { amount currencyCode } balance { amount currencyCode } }';

/* Render gift-card pills from the cart's applied cards (id → removal target,
   lastCharacters masked, amountUsed shown as the per-card deduction). */
function gcRenderPills(cards) {
  const ul = document.querySelector('[data-gift-card-codes]');
  if (!ul) return;
  const form = gcForm();
  const removeLabel = (form && form.dataset.removeGiftCardLabel) || 'Remove gift card';
  ul.innerHTML = cards.map((g) => {
    const usedCents = Math.round(Number((g.amountUsed && g.amountUsed.amount) || 0) * 100);
    const amount = usedCents > 0 ? `<span class="cart-discount__pill-amount">-${gcMoney(usedCents, false)}</span>` : '';
    const last4 = g.lastCharacters || '';
    return `
    <button type="button" class="cart-discount__pill cart-discount__pill--gift-card"
      data-gift-card-remove data-gift-card-id="${gcEscape(g.id)}"
      aria-label="${gcEscape(removeLabel)} ${gcEscape(last4)}">
      <span class="cart-discount__pill-code">&bull;&bull;&bull;&bull; ${gcEscape(last4)}</span>
      ${amount}
      <svg class="cart-discount__pill-remove" width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6.34 17.66 17.66 6.34M6.34 6.34l11.32 11.32" stroke="currentColor" stroke-width="2"/>
      </svg>
    </button>`;
  }).join('');
}

/* Fold the total gift-card deduction into the compared-price subtotal:
   final = discounted total (Liquid data-discounted-cents) − Σ amountUsed.
   Passing 0 resets it to the discount-only baseline. */
function gcRenderSubtotal(usedCents) {
  const el = document.querySelector('[data-cart-subtotal]');
  if (!el) return;
  const now = el.querySelector('[data-subtotal-now]');
  if (!now) return;
  const original = Number(el.dataset.originalCents || '0');
  const discounted = Number(el.dataset.discountedCents || '0');
  const final = Math.max(0, discounted - (usedCents || 0));
  now.textContent = gcMoney(final, true);
  const was = el.querySelector('[data-subtotal-was]');
  if (was) {
    was.textContent = gcMoney(original, true);
    was.hidden = !(final < original);
  }
}

function gcClearUI() {
  gcRenderPills([]);
  gcRenderSubtotal(0);
}

function gcSumUsed(cards) {
  return cards.reduce((s, g) => s + Math.round(Number((g.amountUsed && g.amountUsed.amount) || 0) * 100), 0);
}

/* Paint from a set of applied cards; keep the perf flag in sync with reality. */
function gcPaint(cards) {
  const active = (cards || []).filter(gcCardActive);
  if (!active.length) { gcFlagSet(false); gcClearUI(); return; }
  gcFlagSet(true);
  gcRenderPills(active);
  gcRenderSubtotal(gcSumUsed(active));
}

/* Read the session cart's real applied gift cards and paint. Source of truth.
   Called at the end of renderSections (every cart mutation) + on load/pageshow.
   Gated by the perf flag so carts that never used a gift card do no network. */
async function applyGiftCardState() {
  if (!gcFlagRead()) return; // never applied on this browser → leave Liquid as-is
  const cartId = await gcSessionCartId();
  if (!cartId) return;
  const my = ++gcRun;
  const json = await gcGraphQL(`query($id:ID!){cart(id:$id){${GC_CARD_FIELDS}}}`, { id: cartId });
  if (my !== gcRun) return;
  const cart = json && json.data && json.data.cart;
  const cards = (cart && cart.appliedGiftCards) || [];
  gcPaint(cards);
}

/* Apply a code to the session cart. Returns true if Shopify accepted it. */
async function tryGiftCard(code) {
  const cartId = await gcSessionCartId();
  if (!cartId) return false;
  gcRun++; // supersede any in-flight read
  const json = await gcGraphQL(
    `mutation($cartId:ID!,$codes:[String!]!){cartGiftCardCodesAdd(cartId:$cartId,giftCardCodes:$codes){cart{${GC_CARD_FIELDS}} userErrors{field message} warnings{code message}}}`,
    { cartId, codes: [code] },
  );
  const payload = json && json.data && json.data.cartGiftCardCodesAdd;
  const cards = ((payload && payload.cart && payload.cart.appliedGiftCards) || []).filter(gcCardActive);
  if (!cards.some((g) => gcMatchesLast(g, code))) return false;
  gcPaint(cards);
  return true;
}

/* Remove one applied gift card by its AppliedGiftCard id (real removal on the
   session cart → the native checkout reflects it). */
async function gcRemove(giftCardId) {
  const cartId = await gcSessionCartId();
  if (!cartId) return;
  gcRun++; // supersede any in-flight read
  const json = await gcGraphQL(
    `mutation($cartId:ID!,$ids:[ID!]!){cartGiftCardCodesRemove(cartId:$cartId,appliedGiftCardIds:$ids){cart{${GC_CARD_FIELDS}} userErrors{field message}}}`,
    { cartId, ids: [giftCardId] },
  );
  const payload = json && json.data && json.data.cartGiftCardCodesRemove;
  const cards = ((payload && payload.cart && payload.cart.appliedGiftCards) || []).filter(gcCardActive);
  gcPaint(cards);
}

/* Isolation wrapper: swallow any synchronous throw AND async rejection so a
   gift-card failure can never break a caller (the core cart render, load, etc.). */
function gcSafeApply() {
  try {
    const p = applyGiftCardState();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch (e) { /* gift-card UI must never break the cart */ }
}

/* Delegated gift-card pill removal (pills are JS-rendered, so they aren't bound
   at connectedCallback time). Checkout stays NATIVE — its href is never touched,
   so the market/language in the URL and the on-cart gift card all carry through.
   Wrapped so a removal failure never affects other click handlers on document. */
document.addEventListener('click', (e) => {
  try {
    const removePill = e.target.closest('[data-gift-card-remove]');
    if (!removePill) return;
    e.preventDefault();
    const id = removePill.dataset.giftCardId;
    if (id) gcRemove(id).catch(() => {});
  } catch (e2) { /* never break sibling click handlers */ }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', gcSafeApply);
} else {
  gcSafeApply();
}
// bfcache restore (Back button from checkout) doesn't refire DOMContentLoaded.
window.addEventListener('pageshow', (e) => { if (e.persisted) gcSafeApply(); });
